import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { InFieldLocationConfig } from '@cognite/apm-client';
import type { LocationFilterDTO } from '@cognite/apm-config';
import { makeToast } from '@cognite/cogs-lab-v11';

import { useUpsertInfieldLocationConfig } from '../../app-config/hooks/use-mutation/use-upsert-infield-location-config-mutation/use-upsert-infield-location-config-mutation';
import { useInFieldLocationConfig } from '../../app-config/hooks/use-query/use-infield-location-config';

import * as S from './elements';
import { useADSUpsertLocation } from './hooks/use-mutations/use-upsert-ads-location-config';
import { LocationGeneral } from './location-general/location-general';
import { LocationHeader } from './location-header/location-header';
import { LocationInfieldAssetExplorer } from './location-infield-asset-explorer/location-infield-asset-explorer';
import { LocationInfieldInstanceSpace } from './location-infield-instance-space/location-infield-instance-space';
import { LocationInstanceSpaces } from './location-instance-spaces/location-instance-spaces';
import { LocationMenu } from './location-menu/location-menu';
import { LocationViewMappings } from './location-view-mappings/location-view-mappings';
import type { LocationMenuItems } from './types';

interface Props {
  selectedLocation: LocationFilterDTO | undefined;
  onNavigateToLocationList: () => void;
}

export const LocationView: FC<Props> = ({
  selectedLocation,
  onNavigateToLocationList,
}) => {
  const [activeMenu, setActiveMenu] = useState<LocationMenuItems>(
    'locationDescription'
  );
  const { t } = useTranslation('configuration');

  const { data: infieldLocationConfig, isLoading: isInfieldConfigLoading } =
    useInFieldLocationConfig(selectedLocation?.externalId);

  const { mutateAsync: upsertADSLocation, isPending: isUpsertingADSLocation } =
    useADSUpsertLocation();
  const {
    mutateAsync: upsertInFieldLocation,
    isPending: isUpsertingInfieldLocation,
  } = useUpsertInfieldLocationConfig();

  const handleSaveADSLocation = (location: LocationFilterDTO) => {
    if (!location) return;
    const nextLocation = {
      ...location,
      createdTime: undefined,
      lastUpdatedTime: undefined,
    };

    upsertADSLocation({
      nextLocation,
    });
  };

  const handleSaveInfieldLocation = async (
    nextConfig?: InFieldLocationConfig
  ) => {
    if (!nextConfig) return;
    try {
      await upsertInFieldLocation([nextConfig]);
    } catch {
      makeToast({
        type: 'danger',
        body: t(
          'INFIELD_LOCATION_CONFIG_UPSERT_ERROR',
          'Failed to update InField location configuration'
        ),
      });
    }
  };

  const pageContent = () => {
    switch (activeMenu) {
      case 'locationDescription':
        return (
          <LocationGeneral
            location={selectedLocation}
            isSaving={isUpsertingADSLocation}
            onSave={handleSaveADSLocation}
          />
        );
      case 'viewMappings':
        return (
          <LocationViewMappings
            location={selectedLocation}
            isSaving={isUpsertingADSLocation}
            onSave={handleSaveADSLocation}
          />
        );
      case 'instanceSpaces':
        return (
          <LocationInstanceSpaces
            location={selectedLocation}
            isSaving={isUpsertingADSLocation}
            onSave={handleSaveADSLocation}
          />
        );
      case 'infieldInstanceSpace':
        return (
          <LocationInfieldInstanceSpace
            adsLocationId={selectedLocation?.externalId}
            infieldLocation={infieldLocationConfig}
            onSave={handleSaveInfieldLocation}
          />
        );
      case 'assetExplorer':
        return (
          <LocationInfieldAssetExplorer
            adsLocationId={selectedLocation?.externalId}
            infieldLocation={infieldLocationConfig}
            isSaving={isUpsertingInfieldLocation}
            onSave={handleSaveInfieldLocation}
          />
        );
      default:
        return (
          <LocationGeneral
            location={selectedLocation}
            isSaving={isUpsertingADSLocation}
            onSave={handleSaveADSLocation}
          />
        );
    }
  };

  return (
    <S.LocationContainer direction="column">
      <LocationHeader
        onNavigateToLocationList={onNavigateToLocationList}
        location={selectedLocation}
      />
      <S.LocationContainer>
        <LocationMenu
          activeMenu={activeMenu}
          isInfieldConfigLoading={isInfieldConfigLoading}
          setActiveMenu={setActiveMenu}
        />
        {pageContent()}
      </S.LocationContainer>
    </S.LocationContainer>
  );
};
